import { SendOutlined } from "@ant-design/icons";
import { BaseAnimation, TapAnimation } from "@di-devs/base-components";
import {
	AUTH_MESSAGES,
	SYSTEM_MESSAGES,
	ThemeDefaults,
} from "@di-devs/constants";
import type { Chat, Member } from "@di-devs/member-feature-plugin/shared";
import {
	useBetterFeatureClient,
	useIsMountedRef,
	useSSEEvent,
} from "@di-devs/utils-fe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Col, Form, Input, Modal, message, Row, Tooltip } from "antd";
import { type AxiosError, HttpStatusCode } from "axios";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { MESSAGING_MESSAGES } from "../../../shared/constants/messages/messaging.messages";
import { SSE } from "../../../shared/constants/sse.constants";
import {
	MESSAGE_SESSION_ERROR_KEY,
	MESSAGE_SYSTEM_ERROR_KEY,
} from "../../shared/constants/messages.constants";
import { QUERY_KEYS } from "../../shared/constants/queries.keys";
import {
	ADMIN_LOGIN_ROUTE,
	PERMISSION_ERROR_ROUTE,
} from "../../shared/constants/routes.constants";
import type { FeatureClient } from "../../shared/types";
import { ManagerChatMessage, UserChatMessage } from "../components";

const { TextArea } = Input;

type MemberWithName = Member & { firstName?: string; lastName?: string };

interface ChatModalProps {
	publicSettings: any;
	currentMember: Member | null;
	isChatModalVisible: boolean;
	hideChatModal: () => void;
}

const ChatModal = (props: ChatModalProps) => {
	const { publicSettings, currentMember, isChatModalVisible, hideChatModal } =
		props;

	const isMountedRef = useIsMountedRef();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>();

	const currentMemberRef = useRef<Member | null>(null);
	const messageContainerRef = useRef<any>(null);
	const [chatForm] = Form.useForm();

	const [chats, setChats] = useState<Chat[]>([]);

	const scrollToMessageContainer = () => {
		if (messageContainerRef.current) {
			setTimeout(() => {
				messageContainerRef?.current?.scrollIntoView({ behavior: "smooth" });
			}, 100);
		}
	};

	const getChatQuery = useQuery({
		queryKey: [QUERY_KEYS.CHATS, currentMember?.memberId],
		queryFn: () => reactFeatureClient.getChat(currentMember?.memberId),
		enabled: !!(currentMember?.memberId && isChatModalVisible),
	});

	if (isMountedRef.current && getChatQuery.data?.data?.chats) {
		setChats(getChatQuery.data?.data?.chats || []);
		scrollToMessageContainer();
	}

	if (getChatQuery.data?.error?.status === HttpStatusCode.Unauthorized) {
		navigate(PERMISSION_ERROR_ROUTE);
	} else if (getChatQuery.data?.error?.status === HttpStatusCode.Forbidden) {
		message.warning({
			content: AUTH_MESSAGES.ERROR.SESSION_EXPIRED,
			key: MESSAGE_SESSION_ERROR_KEY,
		});
		navigate(ADMIN_LOGIN_ROUTE);
	} else if (
		getChatQuery.data?.error?.status === HttpStatusCode.InternalServerError
	) {
		message.error({
			content: SYSTEM_MESSAGES.ERROR.SYSTEM_ERROR,
			key: MESSAGE_SYSTEM_ERROR_KEY,
		});
	} else {
		message.error({
			content: SYSTEM_MESSAGES.ERROR.SYSTEM_ERROR,
			key: MESSAGE_SYSTEM_ERROR_KEY,
		});
	}

	const sendChatMutation = useMutation({
		mutationFn: (data: { contents: string; memberId: Member["memberId"] }) =>
			reactFeatureClient.replyChat(data.memberId, {
				contents: data.contents,
				contentType: "text",
			}), // TODO: Support other content types
		onSuccess: () => {
			message.success(MESSAGING_MESSAGES.SUCCESS.MESSAGE_SENT);
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHATS] });
			chatForm.resetFields();
		},
		onError: (error: AxiosError) => {
			if (error?.response?.status === HttpStatusCode.Unauthorized) {
				navigate(PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === HttpStatusCode.Forbidden) {
				message.warning({
					content: AUTH_MESSAGES.ERROR.SESSION_EXPIRED,
					key: MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(ADMIN_LOGIN_ROUTE);
			} else if (
				error?.response?.status === HttpStatusCode.InternalServerError
			) {
				message.error({
					content: SYSTEM_MESSAGES.ERROR.SYSTEM_ERROR,
					key: MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: SYSTEM_MESSAGES.ERROR.SYSTEM_ERROR,
					key: MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	useEffect(() => {
		currentMemberRef.current = currentMember;
	}, [currentMember]);

	const chatHandler = useCallback((data: { memberId?: number }) => {
		if (data !== undefined && Object.keys(data).length !== 0) {
			if (data?.memberId === currentMemberRef.current?.memberId) {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.CHATS],
				});
			}
		}
	}, []);

	useSSEEvent(SSE.CHAT, chatHandler);

	const handleChat = (data: any) => {
		const paramData = {
			contents: data?.contents,
			memberId: currentMember?.memberId,
		};

		sendChatMutation.mutate(paramData);
	};

	return (
		<Modal
			title={`${
				(currentMember as MemberWithName)?.lastName &&
				(currentMember as MemberWithName)?.firstName
					? `${(currentMember as MemberWithName)?.lastName || "ー"} ${(currentMember as MemberWithName)?.firstName || "ー"}`
					: currentMember?.displayName || "ー"
			}様`}
			open={isChatModalVisible}
			onCancel={hideChatModal}
			footer={null}
			centered
			destroyOnClose
		>
			<BaseAnimation>
				<motion.div
					className="pb-4 overflow-auto"
					style={{
						minHeight: "60vh",
						maxHeight: "60vh",
					}}
				>
					{chats.map((chat) => (
						<Row key={chat?.chatId}>
							<Col span={22} offset={chat?.source === "user" ? 0 : 2}>
								<div className="m-1">
									{chat?.source === "user" ? (
										<UserChatMessage
											chat={chat}
											currentMember={currentMember}
										/>
									) : (
										<ManagerChatMessage
											chat={chat}
											publicSettings={publicSettings}
										/>
									)}
								</div>
							</Col>
						</Row>
					))}
					<div ref={messageContainerRef}></div>
				</motion.div>
				<div
					className="py-4 border-t-2 border-solid"
					style={{
						borderColor: publicSettings?.PRIMARY_COLOR?.valueString,
					}}
				>
					<Row>
						<Col span={24}>
							<Form
								form={chatForm}
								onFinish={handleChat}
								size="large"
								colon={false}
								requiredMark={false}
								initialValues={{
									contents: undefined,
								}}
							>
								<Row align="top" gutter={[8, 8]}>
									<Col span={24}>
										<div className="flex justify-end">
											<Tooltip title="送信" placement="top">
												<TapAnimation>
													<Button
														type="primary"
														size="large"
														style={{
															borderColor: ThemeDefaults.WHITE_COLOR,
														}}
														icon={
															<SendOutlined
																style={{
																	color: ThemeDefaults.WHITE_COLOR,
																}}
															/>
														}
														htmlType="submit"
														loading={sendChatMutation.isPending}
													/>
												</TapAnimation>
											</Tooltip>
										</div>
									</Col>
									<Col span={24}>
										<Form.Item
											className="block"
											name="contents"
											rules={[
												{
													required: true,
													whitespace: true,
													message: "メッセージを入力してください",
												},
											]}
										>
											<TextArea
												allowClear
												bordered
												showCount
												autoSize={{ minRows: 5, maxRows: 10 }}
												maxLength={5000}
												placeholder="メッセージを入力してください"
												autoFocus={true}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</Col>
					</Row>
				</div>
			</BaseAnimation>
		</Modal>
	);
};

export default ChatModal;
